import * as React from 'react'
import { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import Icon, { IconName } from '../../../../common/src/components/Icon'
import { Spacer, BodyText, InputMed } from '../../../../common/src/components/core'
import { ColorName, Colors } from '../../../../common/src/constants'
import { Validator } from '../../../../common/src/validate'

/**
 * Represents a field, usually editable.
 *
 * For example, maxAcceptedPerShowing is a field of type T = number.
 */
export interface Field<T> {
  /**
   * The key, eg "maxAcceptedPerShowing"
   *
   * Must exist on both the model (eg task.maxAcceptedPerShowing)
   * and any parent or model if it cascades (eg firm.maxAcceptedPerShowing).
   */
  key: string
  /**
   * Some fields are only editable by admins, etc.
   */
  isEditable?: boolean
  /**
   * The header label for big edit boxes. For example, "Co-signer Policy"
   *
   * Small edit boxes don't have a label. They display the value only.
   */
  label?: string | null
  /**
   * Text to show if a cascading field is not set on the current model, but also not missing, because it's getting a default value from the parent model.
   *
   * For example "Max per showing: see firm"
   */
  defaultToParentLabel?: ((arg?: T) => string) | null
  /**
   * Text to show if a cascading field is overridden by a child model.
   *
   * For example "Max per showing: see task"
   */
  overridenByChildLabel?: string | null
  /**
   * Optional function to display values when not editing.
   *
   * For small edit boxes, this must serve as *both* a label and a value.
   *
   * For example: textFunc: (isFurnished => isFurnished ? "Furnished" : "Unfurnished")
   */
  textFunc?: ((arg?: T) => string) | null
  /**
   * Optional function to display values when editing.
   *
   * For example: textFunc: (isFurnished => isFurnished ? "yes" : "no")
   */
  editTextFunc?: ((arg?: T) => string) | null
  /**
   * Optional function to color values when not editing.
   *
   * For example color Func: (allowSec8 == null ? 'coral' : undefined)
   *
   * (Red / coral highlight on missing values.)
   */
  colorFunc?: (arg?: T) => ColorName | undefined
  /**
   * Optional function to add an icon. See IconName.
   */
  iconFunc?: (arg: T) => IconName | undefined
  /**
   * Validator to turn a string back into a T, for editing.
   */
  validator?: Validator<T> | null
  /**
   * Options to allow one of n choices, for example for enums.
   */
  options?: T[] | null
  /**
   * For use with options. Whether to accept empty string "" to indicate no choice.
   */
  isOptional?: boolean | null
  /**
   * Placeholder when entering edit mode, eg "yes or no"
   */
  placeholder?: string
  /**
   * Tooltip when hovering over an editbox and not in edit mode, for example:
   *
   * "A unit is furnished if it includes beds, couches etc. Fridge and stove only means unfurnished."
   */
  tooltip?: string | null
}

export type CascadeState = 'OWN_VALUE' | 'OVERRIDDEN_BY_CHILD' | 'DEFAULT_TO_PARENT' | 'MISSING'

export default class EditBox<T> extends React.PureComponent<{
  val: T
  cascadeState?: CascadeState
  field: Field<T>
  isEditing: boolean
  hasError: boolean
  icon?: IconName
  onFocus: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur: () => void
  disableEdit?: boolean // if true, disable edits regardless of field.isEditable
  color?: ColorName
}> {
  render() {
    const { val, field, isEditing, cascadeState, hasError, onFocus, onKeyDown, onBlur, icon, disableEdit, color } =
      this.props

    if (field.textFunc == null && val == null) {
      return null
    }
    const { displayColor, displayStr } = this.calcEditBoxText(field, val, cascadeState)

    const isEditable = disableEdit ? false : field.isEditable
    const tooltip = field.tooltip || field.options

    return (
      <InfoBoxPellet
        color={color || displayColor || undefined}
        isEditable={isEditable}
        onFocus={isEditable ? onFocus : undefined}
        title={tooltip != null ? '' + tooltip : undefined}
        tabIndex={isEditable && !isEditing ? 0 : -1}
      >
        {icon && <Icon name={icon} />}
        {icon && <Spacer width={8} />}
        {displayStr}
        {isEditing && this.renderEditInput(field, val, displayStr, hasError, onKeyDown, onBlur)}
        {tooltip && !isEditing && <InfoBoxAsterisk>*</InfoBoxAsterisk>}
      </InfoBoxPellet>
    )
  }

  private inputRefBound = (e: HTMLInputElement) => {
    if (e == null) {
      return
    }
    e.focus()
    e.select()
  }

  private renderEditInput(
    field: Field<T>,
    val: T,
    displayStr: string,
    hasError: boolean,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    onBlur: () => void
  ): ReactNode {
    const editStr = field.editTextFunc ? field.editTextFunc(val) : displayStr
    return (
      <InfoBoxInput
        ref={this.inputRefBound}
        hasError={hasError}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        defaultValue={editStr}
        placeholder={field.placeholder || 'Enter value'}
        tabIndex={-1}
      />
    )
  }

  private calcEditBoxText(
    field: Field<T>,
    val: T,
    cascadeState?: CascadeState
  ): { displayColor?: ColorName; displayStr: string } {
    let displayStr = field.textFunc == null ? '' + val : field.textFunc(val)
    let displayColor: ColorName | undefined
    if (cascadeState === 'MISSING') {
      displayColor = 'midway'
    } else if (cascadeState === 'DEFAULT_TO_PARENT') {
      displayColor = 'midway'
      displayStr = field.defaultToParentLabel == null ? '(set by parent)' : field.defaultToParentLabel(val)
    } else if (cascadeState === 'OVERRIDDEN_BY_CHILD') {
      displayColor = 'midway'
      displayStr = field.overridenByChildLabel || '(set by child)'
    } else {
      displayColor = field.colorFunc == null ? undefined : field.colorFunc(val)
    }
    return { displayColor, displayStr }
  }
}

export const InfoBoxPellet = styled(BodyText)`
  ${(props: { isBold?: boolean; isEditable?: boolean; hoverColor?: ColorName; color?: ColorName }) => css`
    position: relative;
    display: inline-block;
    white-space: nowrap;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: ${props.isBold ? 500 : 400};
    margin: 0 8px 8px 0;
    padding: 8px 12px 6px 12px;
    border-radius: 4px;
    color: ${Colors[props.color || 'silver']};
    background-color: ${Colors.charcoal};
    cursor: ${props.isEditable ? 'pointer' : 'inherit'};
    user-select: all;
    vertical-align: middle;
    &:hover {
      color: ${props.hoverColor ? Colors[props.hoverColor] : ''};
      background-color: ${Colors.charcoal};
    }
    &:focus {
      outline: none;
    }
  `};
`

export const InfoBoxInput = styled(InputMed)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background-color: ${Colors.ink};
  border-radius: 4px;
  padding: 1px 0 0 11px;
  font-size: 14px;
`

export const InfoBoxAsterisk = styled(BodyText)`
  position: absolute;
  font-size: 10px;
  top: 2px;
  right: 1px;
  width: 8px;
`
